import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Modal, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ColumnDefinition, Options } from "tabulator-tables";
import { Grid } from "../../components/Grid";
import { useFormatter } from "../../hooks/useFormatter";
import { useRouter } from "../../hooks/useRouter";
import { PTag } from "../../trendrating/api/PTag";
import { deepClone } from "../../trendrating/deepClone";
import { environmentTemplate, paths } from "../../types/Defaults";
import { ProductInfo } from "../../types/Product";
import { getOrderedKeys } from "../ProductList";
import { RankStrategies } from "../ProductListNew";

type ProductListItemsProps = {
  filter: any;
  products: ProductInfo[];
  setCurrentSort: (value: any) => void;
  hasMarginTop?: boolean;
};

export const ProductListTableItems = forwardRef<any, any>(
  (
    {
      filter,
      products,
      setCurrentSort,
      hasMarginTop = true,
    }: ProductListItemsProps,
    ref,
  ) => {
    const formatter = useFormatter();
    const { t /*, i18n */ } = useTranslation();
    const { history } = useRouter();
    const [rows, setRows] = useState<any>([]);
    const [showMobileNotice, setShowMobileNotice] = useState(false);
    const [initialSort, setInitialSort] = useState<any>({
      field: "name",
      dir: "asc",
    });

    // Find maximum row height
    // const totalRows = useMemo(
    //     () =>
    //         products.reduce(
    //             (maxLength: number, item: ProductInfo) =>
    //                 Math.max(Object.keys(item.description).length, maxLength),
    //             0
    //         ),
    //     [products]
    // );

    // const callback = useCallback((event: any) => {
    //     console.log(event);
    // }, []);

    // TODO refactor and remove it
    // Prepare flat list used for rendering
    // Has objects of objects (geo -> theme -> method)
    // If positions
    const groupedProducts: any = useMemo(() => {
      if (products == null) {
        return {};
      }

      const groupedProducts: any = {};

      /**
       * Data structure:
       *
       * <geo>: {
       *     <theme>: {
       *          <method>: {
       *              single: <item>
       *              30: <item>,...
       *          }
       *     }
       * }
       *
       */

      // Todo can set only the key instead of the full object because
      // there is also the other "products" array with full data.

      let sortedProducts = deepClone(products);
      sortedProducts.sort((a: any, b: any) => {
        let exposureA = a.exposure ?? Infinity;
        let exposureB = b.exposure ?? Infinity;
        if (exposureA > exposureB) {
          return -1;
        } else if (exposureA < exposureB) {
          return 1;
        } else {
          return 0;
        }
      });

      for (let product of sortedProducts) {
        const geo = String(product.geo);
        const theme = String(product.theme);
        const method = String(product.method);
        const rebalance = String(product.rebalance);
        const uiLevel = String(product.uiLevel);
        const position =
          product.positions !== false
            ? String(product.positions)
            : product.positions;
        if (groupedProducts[geo] == null) {
          groupedProducts[geo] = {};
        }

        if (groupedProducts[geo][theme] == null) {
          groupedProducts![geo][theme] = {};
        }
        // if (
        //                 rules != null &&
        //                 product.info.rules != null &&
        //                 rules[product.info.rules] != null
        //             ) {
        //                 products[geo][theme].rules = [...rules[product.info.rules]];
        //                 for (
        //                     let i = rules[product.info.rules].length;
        //                     i < maxRulesLength;
        //                     i++
        //                 ) {
        //                     products[geo][theme].rules.push("");
        //                 }
        //             }

        if (groupedProducts[geo][theme][method] == null) {
          groupedProducts![geo][theme][method] = [];
        }

        if (groupedProducts[geo][theme][method][rebalance] == null) {
          groupedProducts![geo][theme][method][rebalance] = [];
        }

        if (groupedProducts[geo][theme][method][rebalance][uiLevel] == null) {
          groupedProducts![geo][theme][method][rebalance][uiLevel] = [];
        }

        groupedProducts![geo][theme][method][rebalance][uiLevel].push({
          position: position,
          product: product,
        });
      }

      return groupedProducts;
    }, [products]);

    const renderedList = useMemo(() => {
      if (groupedProducts == null) {
        return null;
      }
      const list: any = [];
      getOrderedKeys(groupedProducts, "geo").forEach((geo) =>
        getOrderedKeys(groupedProducts[geo], "theme").forEach((theme) =>
          getOrderedKeys(groupedProducts[geo][theme], "method").forEach(
            (method) =>
              getOrderedKeys(
                groupedProducts[geo][theme][method],
                "rebalance",
              ).forEach((rebalance) => {
                getOrderedKeys(
                  groupedProducts[geo][theme][method][rebalance],
                  "uiLevel",
                ).forEach((uiLevel) => {
                  // groupedProducts[geo][theme][method] does contain
                  // an array, like this:
                  //
                  // {position: ..., product: ...}
                  // Position can be "false", or a number (as string) of
                  // stocks.

                  // Iterate all elements inside method to categorize
                  // of "single item" or grouped stocks
                  // Single items are without number of stocks, displayed
                  // as a single box.

                  let analytics = null;

                  for (const item of groupedProducts[geo][theme][method][
                    rebalance
                  ][uiLevel]) {
                    let newItem = deepClone(item.product);

                    let isVisible = true;
                    if (filter?.geo?.length > 0) {
                      isVisible = isVisible && filter.geo.includes(newItem.geo);
                    }
                    if (filter?.theme?.length > 0) {
                      isVisible =
                        isVisible && filter.theme.includes(newItem.theme);
                    }
                    if (filter?.method?.length > 0) {
                      isVisible =
                        isVisible && filter.method.includes(newItem.method);
                    }
                    if (filter?.rebalance?.length > 0) {
                      isVisible =
                        isVisible &&
                        filter.rebalance.includes(newItem.rebalance);
                    }
                    if (filter?.type?.length > 0) {
                      isVisible =
                        isVisible && filter.type.includes(newItem.type);
                    }
                    if (filter?.uiLevel?.length > 0) {
                      isVisible =
                        isVisible && filter.uiLevel.includes(newItem.uiLevel);
                    }

                    if (!isVisible) {
                      continue;
                    }

                    analytics = new PTag(item.product.strategyAnalytics);

                    newItem.type = "Stock"; // Used for formatter purposes: the formatter needs this

                    newItem.oneYearPerf = analytics.get(
                      "performance_equity_1_Y",
                      "H",
                    );
                    newItem.three_years_perf = analytics.get(
                      "three_years_return_annualized",
                      "H",
                    );
                    newItem.ten_years_perf = analytics.get(
                      "ten_years_return_annualized",
                      "H",
                    );
                    newItem.ten_years_perf_diff = analytics.get(
                      "ten_years_return_annualized",
                      "D",
                    );
                    newItem.sharpeRatio = analytics.get("sharpe", "H");
                    newItem.positiveQuartersPerc = analytics.get(
                      "positive_quarters_perc",
                      "H",
                    );
                    newItem.outperformersQuartersPerc = analytics.get(
                      "outperforming_quarters_perc",
                      "H",
                    );
                    newItem.turnoverAnalytic = analytics.get(
                      "yearly_avg_turnover",
                      "H",
                    );
                    newItem.drawDown = analytics.get(
                      "avg_yearly_drawdown",
                      "H",
                    );

                    newItem.isCombined =
                      item.product.type === "combined-product";
                    newItem.YTD = analytics.get("YTD", "H");

                    list.push(newItem);
                  }
                });
              }),
          ),
        ),
      );
      return list;
    }, [filter, groupedProducts]);

    const grid = useRef<any>();
    const currentWeights = useRef<any>({
      drawDown: 0.0,
      sharpeRatio: 0.0,
      ten_years_perf: 0.0,
      turnoverAnalytic: 0.0,
    });

    const rank = useMemo(() => {
      for (const row of renderedList) {
        row["analytics"] = {
          oneYearPerf: row.oneYearPerf,
          three_years_perf: row.three_years_perf,
          ten_years_perf: row.ten_years_perf,
          ten_years_perf_diff: row.ten_years_perf_diff,
          sharpeRatio: row.sharpeRatio,
          positiveQuartersPerc: row.positiveQuartersPerc,
          outperformersQuartersPerc: row.outperformersQuartersPerc,
          turnoverAnalytic:
            row.turnoverAnalytic === "" ? 0 : row.turnoverAnalytic,
          drawDown: row.drawDown === "" ? 0 : row.drawDown,
          YTD: row.YTD,
        };
      }

      return new RankStrategies(renderedList, currentWeights.current);
    }, [renderedList]);

    const handleChangeRank = useCallback(
      (weights) => {
        for (const key in weights) {
          currentWeights.current[key] = weights[key];
        }

        const ranked = rank?.updateWeights(weights);
        setRows(ranked);

        // insertDataInTable(ranked);
        setCurrentSort({ field: "rank", dir: "asc" });
        setInitialSort({ field: "rank", dir: "asc" });
        // saveLastSort({ field: "rank", direction: "desc" });
        // changeColumns(null, table?.getColumns()[0], "oneYearReturn");
      },
      [rank, setCurrentSort],
    );

    const handleSwitchAbsPerf = useCallback(
      (indicator: string, checked: boolean) => {
        const rankedRows = rank?.switchWeightsPerf(indicator, checked);
        setRows(rankedRows);

        const table = grid.current?.tabulator();
        table?.setSort({ column: "rank", dir: "asc" });
      },
      [rank],
    );

    useImperativeHandle(
      ref,
      () => ({ handleSwitchAbsPerf, handleChangeRank }),
      [handleChangeRank, handleSwitchAbsPerf],
    );

    // Set first data
    useEffect(() => {
      if (rank != null) {
        const rankedRows = rank.byZScore();

        setRows(rankedRows);
        setCurrentSort((current: any) => {
          return { field: "rank", dir: current.dir === "asc" };
        });
      }
    }, [rank, setCurrentSort]);

    const formatPerformanceColored = useCallback(
      (value: any) => {
        return formatter.custom("number", {
          options: {
            colored: "positive",
            hasPositiveSign: true,
            isPercentage: true,
            notAvailable: {
              input: null,
              output: "",
            },
          },
          output: "HTML",
          value: value.value,
          valueHelper: null,
        });
      },
      [formatter],
    );

    const formatPortfolioName = useCallback((data: any) => {
      let name = data.name;
      if (data.geo) {
        name = `<span class='inline-flag flag-${data.geo}'></span> ` + name;
      }
      return name;
    }, []);

    const formatColPDF = useCallback((cell, formatterParams, onRendered) => {
      const data: any = cell.getData();

      const ancorNode = document.createElement("a");
      const iconNode = document.createElement("i");

      const classses = ["bi", "bi-file-earmark-pdf-fill"];
      classses.forEach((className) => {
        iconNode.classList.add(className);
      }, []);
      iconNode.title = "Download factsheets";
      iconNode.style.fontSize = "1.2vw";
      iconNode.style.color = "#F40F02";

      const baseURL = environmentTemplate.api.uri;
      const type = data.isCombined ? "combined-product" : "product";
      const uri = `${baseURL}/${type}/${data.id}/${data.id}_factsheet.pdf`;

      ancorNode.href = uri;

      // Avoid to fire the on click on the table row
      iconNode.addEventListener("click", (event) => {
        event.stopPropagation();
      });

      ancorNode.appendChild(iconNode);
      ancorNode.target = "_blank";

      return ancorNode;
    }, []);

    const sortFn = useCallback((a, b, aR, bR, col, dir) => {
      if (typeof a === "object" && typeof b === "object") {
        return a?.value > b?.value ? 1 : -1;
      }

      return a > b ? 1 : -1;
    }, []);

    const handleHeaderClick = useCallback(
      (event, col) => {
        setCurrentSort((current: any) => {
          const field = col.getDefinition().field;
          if (current.field === field) {
            return { field, dir: current.dir === "asc" ? "desc" : "asc" };
          } else {
            return { field, dir: "desc" };
          }
        });
      },
      [setCurrentSort],
    );

    const columns: ColumnDefinition[] = useMemo(() => {
      // const formatPerformance = (value: any) => {
      //     return formatter.custom("number", {

      //         options: {
      //             hasPositiveSign: true,
      //             isPercentage: true,
      //             notAvailable: {
      //                 input: null,
      //                 output: "",
      //             },
      //         },
      //         output: "HTML",
      //         value: value,
      //         valueHelper: null,
      //     });
      // };

      const formatNumber = (value: any) => {
        return formatter.custom("number", {
          options: {
            decimals: 4,
            notAvailable: {
              input: null,
              output: "",
            },
          },
          output: "HTML",
          value: value.value,
          valueHelper: null,
        });
      };

      const formatPercentage = (value: any) =>
        formatter.custom("number", {
          options: {
            isPercentage: true,
            decimals: 2,
            notAvailable: {
              input: null,
              output: "",
            },
          },
          output: "HTML",
          value: value.value,
          valueHelper: null,
        });

      return [
        {
          title: "Click column header to sort.",
          cssClass: "no-separator",
          resizable: false,
          columns: [
            {
              field: "rank",
              title: "Rank",
              headerClick: handleHeaderClick,
              sorter: sortFn,
              widthGrow: 0.5,
              minWidth: 40,
              resizable: false,
            },
            {
              // hozAlign: "right",
              vertAlign: "top",
              field: "name",
              title: "PORTFOLIO",
              headerClick: handleHeaderClick,
              sorter: sortFn,
              minWidth: 300,
              resizable: false,

              // widthGrow: 3,
              // headerSort: true,
              // headerClick: (e, column: any) => {
              //   const table = column.getTable();
              //   const currentSort = table.getSorters();
              //   const nameSort = currentSort.find((s: any) => s.field === "name");
              //   const isAsc = nameSort?.dir === "asc";
              //   const nextNameDir = isAsc ? "desc" : "asc";
              //   const nextGeoDir = isAsc ? "asc" : "desc";
              //   table.setSort([
              //     { column: "name", dir: nextNameDir },
              //     { column: "geo", dir: nextGeoDir },
              //   ]);
              //   const titleEl = column
              //     .getElement()
              //     .querySelector(".tabulator-col-title");
              //   if (titleEl) {
              //     const icon = nextNameDir === "asc" ? " ▼" : " ▲";
              //     titleEl.innerHTML = `PORTFOLIO${icon}`;
              //   }
              // },

              formatter: (cell, formatterParams, onRendered) =>
                formatPortfolioName(cell.getData()),
            },
          ],
        },
        {
          field: "geo",
          title: "",
          visible: false,
          resizable: false,
        },
        {
          title: "Performance",
          cssClass: "col-group-custom",
          resizable: false,

          columns: [
            {
              hozAlign: "right",
              headerHozAlign: "right",
              // widthGrow: 1,
              headerClick: handleHeaderClick,
              headerTooltip: () => "sort",
              vertAlign: "top",
              field: "oneYearPerf",
              minWidth: 65,
              resizable: false,

              title: "1 Year",
              headerSortStartingDir: "desc",
              formatter: (cell, formatterParams, onRendered) =>
                formatPerformanceColored(cell.getValue()),
              sorter: sortFn,
              sorterParams: {
                thousandSeparator: ",",
                decimalSeperator: ",",
                alignEmptyValues: "bottom",
              } as any,
            },
            {
              hozAlign: "right",
              headerHozAlign: "right",
              vertAlign: "top",
              field: "three_years_perf",
              headerTooltip: () => "sort",
              minWidth: 65,
              resizable: false,

              // widthGrow: 1,
              title:
                t("3_years_performance") +
                "<br/><span style='font-size: 0.8em'>Annualized</span>",
              headerSortStartingDir: "desc",

              formatter: (cell, formatterParams, onRendered) =>
                formatPerformanceColored(cell.getValue()),
              sorter: sortFn,
              headerClick: handleHeaderClick,

              sorterParams: {
                thousandSeparator: ",",
                decimalSeperator: ",",
                alignEmptyValues: "bottom",
              } as any,
            },

            {
              hozAlign: "right",
              headerHozAlign: "right",
              // widthGrow: 1,
              resizable: false,

              vertAlign: "top",
              field: "ten_years_perf",
              headerTooltip: () => "sort",
              minWidth: 65,
              title:
                t("10_years_performance") +
                "<br/><span style='font-size: 0.8em'>Annualized</span>",
              headerSortStartingDir: "desc",
              formatter: (cell, formatterParams, onRendered) =>
                formatPerformanceColored(cell.getValue()),
              sorter: sortFn,
              headerClick: handleHeaderClick,

              sorterParams: {
                thousandSeparator: ",",
                decimalSeperator: ",",
                alignEmptyValues: "bottom",
              } as any,
            },
            {
              hozAlign: "right",
              headerHozAlign: "right",
              // widthGrow: 1,
              vertAlign: "top",
              resizable: false,

              field: "ten_years_perf_diff",
              headerTooltip: () => "sort",
              minWidth: 75,
              title:
                "Excess Return" +
                "<br/><span style='font-size: 0.8em'>Annualized</span>",
              headerSortStartingDir: "desc",
              formatter: (cell, formatterParams, onRendered) =>
                formatPerformanceColored(cell.getValue()),
              sorter: sortFn,
              headerClick: handleHeaderClick,

              sorterParams: {
                thousandSeparator: ",",
                decimalSeperator: ",",
                alignEmptyValues: "bottom",
              } as any,
            },
            // {
            //   hozAlign: "right",
            //   headerHozAlign: "right",
            //   field: "YTD",
            //   resizable: false,

            //   headerTooltip: () => "sort",
            //   widthGrow: 1,
            //   title: "YTD",
            //   headerSortStartingDir: "desc",

            //   formatter: (cell, formatterParams, onRendered) =>
            //     formatPerformanceColored(cell.getValue()),
            //   sorter: sortFn,
            //   headerClick: handleHeaderClick,

            //   sorterParams: {
            //     thousandSeparator: ",",
            //     decimalSeperator: ",",
            //     alignEmptyValues: "bottom",
            //   } as any,
            // },
          ],
        },
        {
          title: "",
          cssClass: "no-separator",
          columns: [
            {
              hozAlign: "right",
              headerHozAlign: "right",
              vertAlign: "top",
              // widthGrow: 1,
              field: "sharpeRatio",
              minWidth: 55,
              resizable: false,

              headerTooltip: () => "sort",
              headerClick: handleHeaderClick,

              title: t("sharpe"),
              headerSortStartingDir: "desc",

              formatter: (cell, formatterParams, onRendered) =>
                formatNumber(cell.getValue()),
              sorter: sortFn,
              sorterParams: {
                thousandSeparator: ",",
                decimalSeperator: ",",
                alignEmptyValues: "bottom",
              } as any,
            },
          ],
        },
        {
          title: "Quarters",
          cssClass: "col-group-custom",
          resizable: false,

          columns: [
            {
              hozAlign: "right",
              headerTooltip: () => "sort",
              headerClick: handleHeaderClick,

              headerHozAlign: "right",
              vertAlign: "top",
              // widthGrow: 1,
              resizable: false,

              field: "positiveQuartersPerc",
              minWidth: 60,
              title: t("%_positive_q"),
              headerSortStartingDir: "desc",

              formatter: (cell, formatterParams, onRendered) =>
                formatPercentage(cell.getValue()),
              sorter: sortFn,
              sorterParams: {
                thousandSeparator: ",",
                decimalSeperator: ",",
                alignEmptyValues: "bottom",
              } as any,
            },
            {
              hozAlign: "right",
              vertAlign: "top",
              resizable: false,

              headerHozAlign: "right",
              headerTooltip: () => "sort",
              headerClick: handleHeaderClick,

              // widthGrow: 1,
              field: "outperformersQuartersPerc",
              minWidth: 107,
              title: t("%_outperformers_q"),
              headerSortStartingDir: "desc",

              formatter: (cell, formatterParams, onRendered) =>
                formatPercentage(cell.getValue()),
              sorter: sortFn,
              sorterParams: {
                thousandSeparator: ",",
                decimalSeperator: ",",
                alignEmptyValues: "bottom",
              } as any,
            },
            // {
            //   hozAlign: "right",
            //   headerHozAlign: "right",
            //   headerTooltip: () => "sort",
            //   headerClick: handleHeaderClick,

            //   widthGrow: 1.5,
            //   field: "turnoverAnalytic",
            //   title: "Turnover",
            //   formatter: (cell) => {
            //     const v = cell.getValue();

            //     return (v.value * 100).toFixed(2);
            //   },

            //   sorter: sortFn,
            // },
            // {
            //   hozAlign: "right",
            //   headerHozAlign: "right",
            //   headerTooltip: () => "sort",
            //   headerClick: handleHeaderClick,

            //   widthGrow: 1.5,
            //   field: "drawDown",
            //   title: "Risk",
            //   formatter: (cell) => {
            //     const v = cell.getValue();

            //     return (v.value * 100).toFixed(2);
            //   },

            //   sorter: sortFn,
            // },
          ],
        },
        {
          title: "",
          cssClass: "no-separator",
          // widthGrow: 0.5,
          resizable: false,

          columns: [
            {
              headerHozAlign: "center",
              hozAlign: "center",
              vertAlign: "top",
              resizable: false,

              field: "",
              title: "Report",
              minWidth: 15,
              // widthGrow: 0.5,
              formatter: formatColPDF,
            },
          ],
        },
      ];
    }, [
      formatColPDF,
      formatPerformanceColored,
      formatPortfolioName,
      formatter,
      handleHeaderClick,
      sortFn,
      t,
    ]);

    // const preparedColumns = useMemo(
    //     () =>
    //         columns.map<ColumnDefinition>((column: ColumnDefinition) => ({
    //             sorter: "string",
    //             formatter: function (cell, formatterParams, onRendered) {
    //                 return formatter.table(
    //                     cell.getField(),
    //                     "table",
    //                     cell.getData()
    //                 );
    //             },
    //             ...column,
    //         })),
    //     [columns, formatter]
    // );

    // const _dirRef = useRef("desc");
    const mobileRows = useMemo(
      () =>
        rows
          .filter((row: any) => row.uiLevel === "primary")
          .sort((a: any, b: any) => a.name?.localeCompare(b.name)),
      [rows],
    );

    const options: Options = useMemo(
      () => ({
        columnHeaderVertAlign: "bottom",
        layout: "fitColumns",
        rowClick: (e, row) => {
          row.toggleSelect(); //toggle row selected state on row click
          e.stopPropagation();

          // Navigate to the correct element
          const data = row.getData();
          if (data.id) {
            const link = paths.productDetail.replace(":id", data.id);
            history.push(link);
          }
        },
        // dataSorted: (sorters: any, rows) => {
        //   // controlla se il sort attuale è solo su "name"
        //   if (sorters.length === 1 && sorters[0].field === "name") {
        //     const geoDir = _dirRef.current === "asc" ? "desc" : "asc";
        //     _dirRef.current = geoDir;
        //     const table = rows[0]?.getTable?.();
        //     if (table) {
        //       table.setSort([
        //         { column: "name", dir: "asc" },
        //         { column: "geo", dir: geoDir },
        //       ]);
        //     }
        //   }
        // },

        initialSort: [
          { column: initialSort.field, dir: initialSort.dir },
          // { column: "geo", dir: "desc" },
        ],
        selectable: 1,
      }),
      [history, initialSort.dir, initialSort.field],
    );

    return (
      <>
        <Row
          className={hasMarginTop ? "mt-4" : ""}
          style={{ minWidth: 0, flex: 1 }}
        >
          <Col sm={12} style={{ minWidth: 0 }} className="d-none d-md-block">
            <Grid
              ref={grid}
              data={rows}
              className="tabulator-sm"
              columns={columns}
              options={options}
            />
          </Col>
          <Col sm={12} className="d-md-none">
            <Table className="mobile-portfolio-table" size="sm">
              <thead>
                <tr>
                  <th>Portfolio</th>
                  <th className="text-end">{t("ytd")}</th>
                  <th className="text-end">
                    {t("10_years_performance")}
                    <br />
                    <span style={{ fontSize: "0.7em" }}>Annualized</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {mobileRows.map((row: any) => (
                  <tr
                    key={row.id}
                    role="button"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowMobileNotice(true)}
                  >
                    <td
                      dangerouslySetInnerHTML={{
                        __html: formatPortfolioName(row),
                      }}
                    />
                    <td
                      className="text-end"
                      dangerouslySetInnerHTML={{
                        __html: formatPerformanceColored(row.YTD),
                      }}
                    />
                    <td
                      className="text-end"
                      dangerouslySetInnerHTML={{
                        __html: formatPerformanceColored(row.ten_years_perf),
                      }}
                    />
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="text-muted text-center small mobile-portfolio-notice">
              {t("mobile_desktop_message")}
            </p>
          </Col>
        </Row>
        <Modal
          show={showMobileNotice}
          onHide={() => setShowMobileNotice(false)}
          centered
        >
          <Modal.Body className="text-center">
            {t("mobile_desktop_message")}
          </Modal.Body>
        </Modal>
      </>
    );
  },
);
